import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
  exhaustMap,
  mergeMap,
  debounceTime,
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { of } from 'rxjs';

import { APIService } from '../../services/api/api.service';
import { downloadCSV } from '@care-portals/shared';
import { DrawerService } from '../../services/drawer/drawer.service';
import { DrawerType } from '@care-portals/api-interfaces';
import { ListQueryService } from '../../components/common/pagination/list-query.service';
import { listInvalidationEffect } from '../../components/common/pagination/list-query-invalidation.effect';
import { SUBSCRIPTION_LIST_QUERY_PREFIX } from './subscriptions.query-keys';

// import * as fromSubscriptions from './subscriptions.reducer';
import * as SubscriptionsActions from './subscriptions.actions';
import * as SubscriptionsSelectors from './subscriptions.selectors';

@Injectable()
export class SubscriptionsEffects {
  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private store: Store,
    private drawerService: DrawerService,
    private listQuery: ListQueryService
  ) { }

  // A subscription mutation can change what the list page shows, so drop the
  // keyed list caches (the offset/SWR path the list page renders from). The
  // embedded cursor lists are unaffected — they read the NgRx collection.
  invalidateListQueries$ = listInvalidationEffect(
    () => this.actions$,
    () => this.listQuery,
    SUBSCRIPTION_LIST_QUERY_PREFIX,
    [
      SubscriptionsActions.createSubscriptionSuccess,
      SubscriptionsActions.updateSubscriptionSuccess,
      SubscriptionsActions.cancelSubscriptionSuccess,
      SubscriptionsActions.applyDiscountForInvoiceSuccess
    ]
  );

  getSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.getSubscription),
      mergeMap(({ subscriptionId }) =>
        this.apiService.getSubscription(subscriptionId).pipe(
          map(subscription =>
            SubscriptionsActions.getSubscriptionSuccess({ subscription })
          ),
          catchError(error =>
            of(SubscriptionsActions.getSubscriptionsFailure({ error }))
          )
        )
      )
    )
  );

  getSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.getSubscriptions),
      withLatestFrom(
        this.store.select(SubscriptionsSelectors.getSubscriptionsState)
      ),
      debounceTime(500),
      switchMap(([action, state]) => {
        // Use filters from action params instead of store state
        const actionFilters = action.params;

        // Get current filters from store for pagination
        const currentFilters = state.filters;

        const limit = actionFilters.limit || currentFilters.limit || 100;
        const currentOffset = currentFilters.offset;
        const offset =
          action.loadType === 'older'
            ? currentOffset + limit
            : action.loadType === 'newer'
              ? 0
              : actionFilters.offset || 0;
        const sortOrder = actionFilters.sortOrder || '-1';
        const sortKey = actionFilters.sortKey || 'nextCycleDate';

        const params = {
          ...action.params,
          limit,
          offset,
          sortOrder,
          sortKey
        };

        return this.apiService.getSubscriptions(params).pipe(
          tap(subscriptionResp => {
            if (!subscriptionResp.data.length && !action.supressNotification) {
              if (action.loadType === 'newer') {
                this.notification.info('Subscriptions', 'You are all caught up!');
              } else {
                this.notification.info(
                  'Subscriptions',
                  'There are no older subscriptions'
                );
              }
            }
          }),
          map(subscriptionResp =>
            SubscriptionsActions.getSubscriptionsSuccess({
              subscriptionResp,
              loadType: action.loadType,
              filters: {
                ...actionFilters,
                limit,
                offset,
                sortKey,
                sortOrder
              }
            })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'Unable to load subscriptions.');
            return of(SubscriptionsActions.getSubscriptionsFailure({ error }));
          })
        );
      })
    )
  );

  cancelSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.cancelSubscription),
      switchMap(action =>
        this.apiService.cancelSubscription(action.id).pipe(
          map(subscription =>
            SubscriptionsActions.cancelSubscriptionSuccess({ subscription })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'Unable to cancel subscription.');
            return of(SubscriptionsActions.cancelSubscriptionFailure({ error }));
          })
        )
      )
    )
  );

  updateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.updateSubscription),
      switchMap(action =>
        this.apiService
          .updateSubscription(action.id, action.updates)
          .pipe(
            map(subscription =>
              SubscriptionsActions.updateSubscriptionSuccess({ subscription })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error(
                'Subscription',
                error?.message || 'Failed to update subscription'
              );
              return of(SubscriptionsActions.updateSubscriptionFailure({ error }));
            })
          )
      )
    )
  );

  updateSubscriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.updateSubscriptionSuccess),
      tap(() => {
        this.notification.success(
          'Subscription Updated',
          'Subscription details saved successfully.'
        );
      }),
      map(() => SubscriptionsActions.closeSubscriptionDetailsDrawer())
    )
  );

  createSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.createSubscription),
      exhaustMap(({ payload }) =>
        this.apiService.createSubscription(payload).pipe(
          map(subscription =>
            SubscriptionsActions.createSubscriptionSuccess({ subscription })
          ),
          catchError(error => {
            console.error('Error creating subscription:', error);
            this.notification.error(
              'Error',
              error?.message || error?.error?.message || 'Unable to create subscription.'
            );
            return of(
              SubscriptionsActions.createSubscriptionFailure({ error })
            );
          })
        )
      )
    )
  );

  createSubscriptionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SubscriptionsActions.createSubscriptionSuccess),
        tap(() => {
          this.notification.success(
            'Subscription Created',
            'Subscription created successfully.'
          );
          this.drawerService.closeDrawer(DrawerType.CREATE_SUBSCRIPTION);
        })
      ),
    { dispatch: false }
  );

  openSubscriptionDetailsDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SubscriptionsActions.openSubscriptionDetailsDrawer),
        tap(({ subscriptionId, onClose }) => {
          this.drawerService.openDrawer({
            name: DrawerType.SUBSCRIPTION_DETAILS,
            props: {
              subscriptionId
            },
            onClose: onClose
          });
        })
      ),
    { dispatch: false }
  );

  closeSubscriptionDetailsDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SubscriptionsActions.closeSubscriptionDetailsDrawer),
        tap(() => {
          this.drawerService.closeDrawer(DrawerType.SUBSCRIPTION_DETAILS);
        })
      ),
    { dispatch: false }
  );

  applyDiscountForSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.applyDiscountForInvoice),
      switchMap(action =>
        this.apiService.applyDiscountForInvoice(action.req).pipe(
          tap(() => {
            this.notification.success('Success', 'Discount applied successfully');
          }),
          switchMap(discount => [
            SubscriptionsActions.applyDiscountForInvoiceSuccess({ discount }),
            SubscriptionsActions.closeDiscountModal()
          ]),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'Unable to apply discount.');
            return of(SubscriptionsActions.applyDiscountForInvoiceFailure({ error }));
          })
        )
      )
    )
  );

  exportSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionsActions.exportSubscriptions),
      switchMap(({ dateRange }) =>
        this.apiService.exportSubscriptions(dateRange).pipe(
          map(csvData =>
            SubscriptionsActions.exportSubscriptionsSuccess({ csvData })
          ),
          catchError(error => {
            console.error('Error exporting subscriptions:', error);
            this.notification.error(
              'Error',
              'There was an error exporting subscriptions.'
            );
            return of(SubscriptionsActions.exportSubscriptionsFailure({ error }));
          })
        )
      )
    )
  );

  downloadExportedSubscriptions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SubscriptionsActions.exportSubscriptionsSuccess),
        tap(({ csvData }) => {
          downloadCSV(csvData, 'subscriptions.csv');
        })
      ),
    { dispatch: false }
  );
}
